<template>
  <table class="entsys--table keep-top" data-test="upcomingDesktopTable">
    <colgroup>
      <col style="width: 250px" />
      <col style="min-width: 100px" />
      <col />
      <col style="min-width: 300px" />
      <col />
      <col style="width: 150px" />
    </colgroup>
    <thead class="text-left">
      <tr>
        <th>
          {{ $t("events:common.Events.label", "Event") }}
        </th>
        <th>
          {{
            $t(
              "events:common.athlete.label",
              "Athlete",
              "Label for athlete in different parts of the system"
            )
          }}
        </th>
        <th>
          {{
            $t(
              "events:common.Horse.label",
              "Horse",
              "Label for horse column in results"
            )
          }}
        </th>
        <th>
          {{
            $t(
              "events:common.Competitions.label",
              "Competitions",
              "Label for competitions in different parts of the system"
            )
          }}
        </th>
        <th>
          {{ $t("events:common.payments.label") }}
        </th>
        <th />
      </tr>
    </thead>
    <tbody>
      <tr v-if="state.loading">
        <td colspan="99" class="pa-0">
          <v-progress-linear :active="state.loading" indeterminate />
        </td>
      </tr>
      <tr v-else-if="state.registrations.length == 0">
        <td colspan="99" class="text-center">
          {{ $t("common:NoResults.messsage") }}
        </td>
      </tr>
      <template v-for="item in state.registrations" :key="item.id">
        <tr :data-scroll-focus="'upcoming-registration-' + item.id">
          <td style="word-break: break-word">
            <div class="my-2">
              {{ $dayjs.nicerDate(item.event.startDate, item.event.endDate) }}
              <div class="my-1">
                <AppShowEvent :event="item.event" />
              </div>

              <div>
                <AppShowDisciplineName
                  :name="item.event.discipline.name"
                  chip
                />
              </div>
            </div>
          </td>

          <td>
            <div v-if="item.event.discipline.name === 'vaulting'" class="my-2">
              <div>
                <div v-if="item.teamName">
                  <b>
                    {{
                      $t(
                        "events:event.masterlist.vaulting.TeamName.column",
                        "Team name",
                        "Team name column heading for vaulting events in masterlist"
                      )
                    }}
                  </b>
                  <br />
                  {{ item.teamName }}
                </div>

                <template
                  v-if="
                    item.vaultingLungerPersonId ||
                    item.vaultingReserveLungerPersonId
                  "
                >
                  <b>
                    {{
                      $t(
                        "events:event.masterlist.vaulting.lunger.column",
                        "Lunger",
                        "Lunger column heading for vaulting events in masterlist"
                      )
                    }}
                  </b>

                  <AppShowPeopleList
                    v-if="item.vaultingLungerPersonId"
                    :people="[item.vaultingLungerPerson]"
                    show-athlete-name-only
                  />

                  <AppShowPeopleList
                    v-if="item.vaultingReserveLungerPersonId"
                    :people="[item.vaultingReserveLungerPerson]"
                    show-athlete-name-only
                  />
                </template>
              </div>
              <b>
                {{
                  $t(
                    "events:common.Athletes.label",
                    "Athlete",
                    "Label for athlete in different parts of the system"
                  )
                }}
              </b>
              <AppShowPeopleList :people="item.athletes" show-athlete-name-only>
                <template #personNameAdditionalDetails="{ person }">
                  <AppShowEntryFeiFlags
                    entity-type="person"
                    :registration-person="
                      item.registrationPeople.find(
                        regPerson => regPerson.personId == person.id
                      )
                    "
                  />
                </template>
              </AppShowPeopleList>
            </div>
            <div v-else class="my-2">
              <AppShowPeopleList
                :people="item.athletes"
                show-athlete-name-only
                :paradressage-grade="item.paradressageGrade"
              >
                <template #personNameAdditionalDetails="{ person }">
                  <AppShowEntryFeiFlags
                    entity-type="person"
                    :registration-person="
                      item.registrationPeople.find(
                        regPerson => regPerson.personId == person.id
                      )
                    "
                  />
                </template>
                <template #personAdditionalDetails>
                  <AppShowCarriageNumber
                    v-if="item.event.discipline.name === 'driving'"
                    :carriage-number="item.carriageNumber"
                  />
                </template>
              </AppShowPeopleList>
            </div>
          </td>
          <!-- horses -->
          <td>
            <div class="my-2">
              <AppHorseSlot v-if="item.horses.some(h => h.isHorseSlot)" badge />
              <template v-else>
                <AppShowHorseName
                  v-for="horse in item.horses"
                  :key="horse.id"
                  :horse="horse"
                  show-identifier
                >
                  <template #horseNameAdditionalDetails>
                    <AppShowEntryFeiFlags
                      entity-type="horse"
                      :registration-horse="
                        item.registrationHorses.find(
                          regHorse => regHorse.horseId == horse.id
                        )
                      "
                    />
                  </template>
                  <template #appendHorseDetails>
                    <AppShowBeddingType
                      :horse="horse"
                      :registration-horses="item.registrationHorses"
                    />
                  </template>
                </AppShowHorseName>
              </template>
            </div>
          </td>

          <td>
            <div class="my-2 text-secondary">
              <RegistrationClasses
                :entries="item.entries"
                :show-cup-season="false"
              />
              <AppShowEntryProducts
                v-if="item.registrationEventProducts.length > 0"
                :use-online-payments="
                  actions.isFeatureFlagSet('useOnlinePayments')
                "
                :registration-event-products="item.registrationEventProducts"
              />
            </div>
          </td>
          <td>
            <UpcomingRegistrationPaymentStatus
              v-bind="{ actions, state, item }"
            />

            <div>
              <AppShowEntryBillingContacts
                :event="item.event"
                :registration="item"
              />
            </div>
          </td>
          <!-- actions -->
          <td class="text-right">
            <div class="my-2">
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'registrationCreateOrUpdate'
                  )
                "
                :to="{
                  name: item.event.noncompetition
                    ? 'NoncompetitionEventRegistrationEditPage'
                    : 'EventRegistrationEditPage',
                  params: {
                    eventId: item.event.id,
                    registrationId: item.id
                  },
                  state: {
                    returnRoute: {
                      name: $route.name,
                      hash: $route.hash,
                      params: {
                        personId: state.personId
                      },
                      state: {
                        routeScrollState: {
                          querySelector: `[data-scroll-focus='upcoming-registration-${item.id}']`
                        }
                      }
                    }
                  }
                }"
                icon
                size="small"
                class="bg-edit-green"
              >
                <v-icon color="white" size="small"> $edit </v-icon>
              </v-btn-icon>
              <v-btn-icon
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'registrationUnregister'
                  )
                "
                icon
                size="small"
                class="bg-remove"
                @click="actions.deleteItem(item, $event)"
              >
                <v-icon size="small" color="white"> $iconTrash </v-icon>
                <v-progress-circular
                  v-show="false"
                  small
                  size="14"
                  color="white"
                  indeterminate
                />
              </v-btn-icon>
              <v-btn-expand
                :model-value="state.expanded.includes(item.id)"
                data-test="expand"
                @click.prevent="actions.expand(item.id)"
              >
              </v-btn-expand>
            </div>
          </td>
        </tr>

        <tr v-if="state.expanded.includes(item.id)">
          <td colspan="99" style="padding: 0 !important">
            <v-sheet color="strong" class="px-6 py-2">
              <v-row dense class="my-4">
                <v-col cols="4">
                  <div>
                    <p>
                      <span>
                        <span class="text-muted bold">
                          {{
                            $t(
                              "events:event.masterlist.RegOtherInfo.registeredby",
                              "Registered by",
                              "Sub-heading in other information section for registrations in masterlist"
                            )
                          }}
                        </span>
                        <AppShowPeopleList
                          :people="[item.registrantPerson]"
                          show-name-only
                        />

                        <template
                          v-if="item.event.discipline.name == 'vaulting'"
                        >
                          {{
                            $t(
                              "events:event.masterlist.RegOtherInfo.groom(s)",
                              "Groom(s):",
                              "Sub-heading in other information section for groom(s) (only for driving) in masterlist"
                            )
                          }}
                          <AppShowDrivingGrooms :registration="item" />
                        </template>
                      </span>
                    </p>
                  </div>

                  <div>
                    <span class="text-muted bold">{{
                      $t(
                        "events:common.competitionDetails.label",
                        "Competition details"
                      )
                    }}</span>
                    <AppShowEntryCompetitionDetails :entries="item.entries" />
                    <br />
                  </div>

                  <template
                    v-if="
                      item.entries.some(
                        x =>
                          x.registeredToFeiCompetitionCodes &&
                          x.registeredToFeiCompetitionCodes.length
                      )
                    "
                  >
                    <div
                      v-for="entry in item.entries.filter(
                        x =>
                          x.registeredToFeiCompetitionCodes &&
                          x.registeredToFeiCompetitionCodes.length
                      )"
                      :key="entry.id + '-feiCode'"
                    >
                      <div class="text-muted bold">
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.selectedCompetitions.heading",
                            "Competitions that were initially selected for entry:"
                          )
                        }}
                      </div>
                      <AppShowMappedFeiCompetitionCodes
                        :entry="entry"
                        :competition="entry.competition"
                      />
                    </div>

                    <small class="text-muted">
                      {{
                        $t(
                          "events:event.masterlist.RegOtherInfo.selectedCompetitions.hint",
                          "To change competitions, delete entry and make a new entry with correct competitions"
                        )
                      }}
                    </small>
                  </template>

                  <AppShowQuotaType
                    v-if="item.entries.some(x => x.ocQuotaType)"
                    :entries="item.entries"
                  />

                  <div v-if="item.event.discipline.name === 'driving'">
                    <p>
                      {{
                        $t(
                          "events:event.masterlist.RegOtherInfo.groom(s)",
                          "Groom(s):",
                          "Sub-heading in other information section for groom(s) (only for driving) in masterlist"
                        )
                      }}
                      <AppShowDrivingGrooms :registration="item" />
                      <br />
                      {{
                        $t(
                          "events:event.masterlist.RegOtherInfo.carriagenr",
                          "Carriage nr:",
                          "Sub-heading in other information section for carriage number (only for driving) in masterlist"
                        )
                      }}
                      {{ item.carriageNumber }}
                      <template v-if="item.drivingCarriageWidth">
                        <br />
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.carriagewidth",
                            "Carriage width:",
                            "Sub-heading in other information section for carriage width (only for driving) in masterlist"
                          )
                        }}
                        {{ item.drivingCarriageWidth }}cm
                      </template>
                    </p>

                    <div>
                      <span class="text-muted bold">Phases</span>

                      <div v-for="(entry, i) in item.entries" :key="i">
                        <b>{{ entry.competition.code }}:</b>
                        {{
                          entry.phasesToParticipate
                            ? entry.phasesToParticipate
                                .map(x =>
                                  $t(
                                    `events:CompetitionForm.possiblePhase.${x}.label`
                                  )
                                )
                                .join(", ")
                            : "None"
                        }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="
                      item.event.useOnlinePaymentsForPrizeMoney &&
                      item.prizeMoneyPayoutMethod
                    "
                  >
                    <PrizeMoneyPayoutContactMasterlist
                      :contact="item.prizeMoneyPayoutContact"
                      :prize-money-payout="{
                        method: item.prizeMoneyPayoutMethod,
                        sportFundAthleteId:
                          item.prizeMoneyPayoutSportFundAthleteId
                      }"
                    />
                  </div>
                </v-col>
                <v-col cols="4">
                  <div v-if="item.logisticNotes">
                    <div class="text-muted bold">
                      {{
                        $t(
                          "events:event.masterlist.RegOtherInfo.ArrivalAndDepartureTime",
                          "Arrival and departure time",
                          "Arrival and departure time sub-heading in other information section for registrations in masterlist"
                        )
                      }}
                    </div>
                    {{ item.logisticNotes }}
                  </div>
                  <div v-if="item.registrantNotes">
                    <div class="text-muted bold">
                      {{
                        $t(
                          "events:event.masterlist.RegOtherInfo.generalnotes",
                          "General notes",
                          "General notes sub-heading in other information section for registrations in masterlist"
                        )
                      }}
                    </div>
                    {{ item.registrantNotes }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div
                    v-for="(entry, i) in actions.registrantComments(
                      item.entries
                    )"
                    :key="'entry-' + entry + i"
                  >
                    <div v-if="entry.noteToFeiOrganiser" class="mb-2">
                      <div class="text-muted bold">
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.competitionNotes.noteToFeiOrganiser.label",
                            {
                              competitionCode: entry.competition.code,
                              defaultValue:
                                "Class \{\{ competitionCode \}\} note to FEI show organizer"
                            }
                          )
                        }}
                      </div>
                      {{ entry.noteToFeiOrganiser }}
                    </div>

                    <div v-if="entry.registrantComment">
                      <div class="text-muted bold">
                        {{
                          $t("events:event.masterlist.RegOtherInfo.class", {
                            competitionCode: entry.competition.code,
                            defaultValue:
                              "Class \{\{ competitionCode \}\} notes"
                          })
                        }}
                      </div>
                      {{ entry.registrantComment }}
                    </div>
                  </div>
                </v-col>
              </v-row>
              <div>
                <h3>
                  {{
                    $t(
                      "events:event.masterlist.RegOtherInfo.timestamps",
                      "Timestamps"
                    )
                  }}
                </h3>
                <table data-test="timestamps">
                  <thead>
                    <tr>
                      <th />
                      <th class="text-left">
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.RegAt",
                            "Registered at"
                          )
                        }}
                      </th>
                      <th class="text-left">
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.LastChange",
                            "Last change"
                          )
                        }}
                      </th>
                      <th class="text-left">
                        {{
                          $t(
                            "events:event.masterlist.RegOtherInfo.PaidAt",
                            "Paid at"
                          )
                        }}
                      </th>
                      <th class="text-left">
                        {{
                          $t(
                            "events:common.UnregisteredAt.label",
                            "Unregistered at"
                          )
                        }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(stamp, stampIndex) in actions.timestamps(item)"
                      :key="'stamp-' + stampIndex"
                    >
                      <td>
                        {{ stamp.name }}
                      </td>
                      <td>
                        {{ $dayjs.date(stamp.createdAt, "fullTime") }}
                      </td>
                      <td>
                        {{ $dayjs.date(stamp.updatedAt, "fullTime") }}
                      </td>
                      <td>
                        {{ $dayjs.date(stamp.paidAt, "fullTime") }}
                      </td>
                      <td>
                        {{
                          stamp.unregisteredAt
                            ? $dayjs.date(stamp.unregisteredAt, "fullTime")
                            : "-"
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-sheet>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>
<script>
import { defineComponent } from "vue";
import AppShowEntryProducts from "@AppShows/event/AppShowEntryProducts.vue";

import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppShowBeddingType from "@AppShows/horse/AppShowBeddingType.vue";
import AppShowDisciplineName from "@AppShows/AppShowDisciplineName.vue";
import RegistrationClasses from "./RegistrationClasses.vue";
import AppHorseSlot from "@elements/AppHorseSlot.vue";
import AppShowMappedFeiCompetitionCodes from "@AppShows/event/AppShowMappedFeiCompetitionCodes.vue";
import AppShowQuotaType from "@AppShows/event/AppShowQuotaType.vue";
import AppShowEntryFeiFlags from "@AppShows/event/AppShowEntryFeiFlags.vue";
import AppShowCarriageNumber from "@AppShows/event/AppShowCarriageNumber.vue";
import UpcomingRegistrationPaymentStatus from "./UpcomingRegistrationPaymentStatus.vue";
import AppShowEntryBillingContacts from "@AppShows/event/AppShowEntryBillingContacts.vue";
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import PrizeMoneyPayoutContactMasterlist from "@elements/contact/PrizeMoneyPayoutContactMasterlist.vue";
import AppShowDrivingGrooms from "@AppShows/event/AppShowDrivingGrooms.vue";
import AppShowEntryCompetitionDetails from "@AppShows/event/AppShowEntryCompetitionDetails.vue";

export default defineComponent({
  name: "UpcomingRegisteredEventsDesktopTable",
  components: {
    AppShowEvent,
    AppShowPeopleList,
    AppShowHorseName,
    AppShowBeddingType,
    RegistrationClasses,
    AppShowDisciplineName,
    AppShowEntryProducts,
    AppHorseSlot,
    AppShowMappedFeiCompetitionCodes,
    AppShowQuotaType,
    AppShowEntryFeiFlags,
    AppShowCarriageNumber,
    UpcomingRegistrationPaymentStatus,
    AppShowEntryBillingContacts,
    PrizeMoneyPayoutContactMasterlist,
    AppShowDrivingGrooms,
    AppShowEntryCompetitionDetails
  },
  props: {
    state: {
      type: Object,
      required: true
    },
    actions: {
      type: Object,
      required: true
    }
  }
});
</script>
